import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, Loader2, Tag, Wand2, Save, Sparkles, Layers, Trash2, Users } from "lucide-react";
import { useChapters } from "@/hooks/useChapters";
import { clearTitlesCache } from "@/hooks/useOfficerTitles";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LIST_META = {
  all_titles: ["All Titles", "Every assignable title — powers the dropdowns across the hub"],
  officer_titles: ["Officers", "Anyone holding one of these counts as an officer"],
  national_officer_titles: ["National Officers", "Can act on National members, manage suggestions, etc."],
  national_admin_titles: ["National Admins", "System group: Permissions Panel, this editor, user management"],
  leadership_core_titles: ["Core Leadership", "Refactor Health, downloads admin, user editing"],
  promotion_eligible_titles: ["Promotion Runners", "Titles allowed to run promotions"],
};

export default function TitleEditor() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const { active: activeChapters } = useChapters();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lists, setLists] = useState({});
  const [labels, setLabels] = useState({});
  const [chapterMap, setChapterMap] = useState({});
  const [addInputs, setAddInputs] = useState({});
  const [ren, setRen] = useState({ old_title: "", new_title: "", label: "" });
  const [renaming, setRenaming] = useState(false);
  const [nt, setNt] = useState({ title: "", label: "", chapters: [] });
  const [creating, setCreating] = useState(false);
  const [usage, setUsage] = useState({});
  const [deleting, setDeleting] = useState("");

  const fetchUsage = () =>
    axios.get(`${BACKEND_URL}/api/officer-titles/usage`, auth)
      .then((res) => setUsage(res.data.usage || {}))
      .catch(() => {});

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/officer-titles`, auth)
      .then((res) => { setLists(res.data.lists || {}); setLabels(res.data.labels || {}); setChapterMap(res.data.chapter_map || {}); })
      .catch(() => toast.error("Failed to load titles"))
      .finally(() => setLoading(false));
    fetchUsage();
    // eslint-disable-next-line
  }, []);

  const allKnown = [...new Set(Object.values(lists).flat())].sort();

  const removeFrom = (name, t) =>
    setLists((l) => ({ ...l, [name]: l[name].filter((x) => x !== t) }));

  const addTo = (name) => {
    const v = (addInputs[name] || "").trim();
    if (!v) return;
    if ((lists[name] || []).includes(v)) return toast.error(`"${v}" is already in this list`);
    setLists((l) => ({ ...l, [name]: [...l[name], v] }));
    setAddInputs((a) => ({ ...a, [name]: "" }));
  };

  const saveLists = async () => {
    if (Object.values(lists).some((v) => !v.length)) return toast.error("Lists cannot be empty");
    setSaving(true);
    try {
      const cmap = {};
      (lists.all_titles || []).forEach((t) => { cmap[t] = chapterMap[t] || []; });
      const res = await axios.put(`${BACKEND_URL}/api/officer-titles`, { lists, labels: {}, chapter_map: cmap }, auth);
      setLists(res.data.lists); setLabels(res.data.labels); setChapterMap(res.data.chapter_map || {});
      clearTitlesCache();
      toast.success("Title lists saved — changes apply everywhere immediately");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const createTitle = async () => {
    if (!nt.title.trim()) return toast.error("Enter the short title");
    setCreating(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/officer-titles/create`, nt, auth);
      setLists(res.data.lists); setLabels(res.data.labels); setChapterMap(res.data.chapter_map || {});
      clearTitlesCache();
      toast.success(
        nt.chapters.length
          ? `"${nt.title.trim()}" created for ${nt.chapters.join(", ")} — it now appears in those chapters' Permissions tabs and member dropdowns`
          : `"${nt.title.trim()}" created for all chapters — it now appears in every Permissions tab and member dropdown`,
        { duration: 7000 }
      );
      setNt({ title: "", label: "", chapters: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const toggleNtChapter = (c) =>
    setNt((p) => ({ ...p, chapters: p.chapters.includes(c) ? p.chapters.filter((x) => x !== c) : [...p.chapters, c] }));

  const toggleMapChapter = (title, c) =>
    setChapterMap((m) => {
      const cur = m[title] || [];
      return { ...m, [title]: cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c] };
    });

  const deleteTitle = async (t) => {
    if (!window.confirm(`Delete "${t}" everywhere? It will be removed from every title list, its label, chapter assignment, and all permission rows. This cannot be undone.`)) return;
    setDeleting(t);
    try {
      const res = await axios.delete(`${BACKEND_URL}/api/officer-titles/${encodeURIComponent(t)}`, auth);
      setLists(res.data.lists); setLabels(res.data.labels); setChapterMap(res.data.chapter_map || {});
      clearTitlesCache();
      fetchUsage();
      toast.success(`"${t}" deleted everywhere (${res.data.permissions_purged || 0} permission rows purged)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed", { duration: 8000 });
    } finally { setDeleting(""); }
  };

  const runRename = async () => {
    if (!ren.old_title || !ren.new_title.trim()) return toast.error("Pick the old title and enter the new one");
    if (!window.confirm(`Rename "${ren.old_title}" to "${ren.new_title}" EVERYWHERE (members, logins, permissions, email groups)?`)) return;
    setRenaming(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/officer-titles/rename`, ren, auth);
      setLists(res.data.lists); setLabels(res.data.labels); setChapterMap(res.data.chapter_map || {});
      clearTitlesCache();
      const m = res.data.migrated || {};
      toast.success(`Renamed! Updated ${m.members || 0} members, ${m.users || 0} logins, ${m.role_permissions || 0} permission roles, ${m.email_groups || 0} email groups`, { duration: 9000 });
      setRen({ old_title: "", new_title: "", label: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    } finally { setRenaming(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" size="icon" onClick={() => navigate("/")}
            className="border-slate-600 text-slate-300 shrink-0" data-testid="titleeditor-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
              <Tag className="w-5 h-5 text-amber-500" /> Title Editor
            </h1>
            <p className="text-slate-400 text-xs sm:text-sm">Rename, add, or remove officer titles — no code changes needed</p>
          </div>
          <Button onClick={saveLists} disabled={saving || loading}
            className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto" data-testid="titleeditor-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save Lists</>}
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <>
            <Card className="bg-slate-800/50 border-emerald-800/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-500" /> Create a New Title
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-xs">Short title</Label>
                    <Input value={nt.title} onChange={(e) => setNt({ ...nt, title: e.target.value })}
                      placeholder="e.g. QM" maxLength={15}
                      className="bg-slate-700 border-slate-600 text-white" data-testid="create-title-input" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">Full label (optional)</Label>
                    <Input value={nt.label} onChange={(e) => setNt({ ...nt, label: e.target.value })}
                      placeholder="e.g. Quartermaster"
                      className="bg-slate-700 border-slate-600 text-white" data-testid="create-title-label-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Held in chapters <span className="text-slate-500">(none selected = all chapters)</span></Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {activeChapters.map((c) => (
                      <button key={c} type="button" onClick={() => toggleNtChapter(c)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                          nt.chapters.includes(c)
                            ? "bg-emerald-600 border-emerald-500 text-white"
                            : "bg-slate-900/60 border-slate-700 text-slate-400 hover:text-white"
                        }`}
                        data-testid={`create-title-chapter-${c}`}>{c}</button>
                    ))}
                  </div>
                </div>
                <Button onClick={createTitle} disabled={creating || !nt.title.trim()}
                  className="bg-emerald-700 hover:bg-emerald-600 text-white w-full sm:w-auto" data-testid="create-title-btn">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create Title</>}
                </Button>
                <p className="text-slate-500 text-xs">
                  The new title is added to the assignable list, shows up in the selected chapters' Permissions tabs, and appears in member Title dropdowns for those chapters only.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-amber-800/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Wand2 className="w-4 h-4 text-amber-500" /> Rename a Title Everywhere
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                <div>
                  <Label className="text-slate-300 text-xs">Current title</Label>
                  <Select value={ren.old_title} onValueChange={(v) => setRen({ ...ren, old_title: v })}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="rename-old-select">
                      <SelectValue placeholder="Pick…" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 text-white border-slate-700">
                      {allKnown.map((t) => (
                        <SelectItem key={t} value={t} className="text-white hover:bg-slate-700">
                          {t}{labels[t] && labels[t] !== t ? ` — ${labels[t]}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">New short title</Label>
                  <Input value={ren.new_title} onChange={(e) => setRen({ ...ren, new_title: e.target.value })}
                    placeholder="e.g. CFO" className="bg-slate-700 border-slate-600 text-white" data-testid="rename-new-input" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Full label (optional)</Label>
                  <Input value={ren.label} onChange={(e) => setRen({ ...ren, label: e.target.value })}
                    placeholder="e.g. Chief Financial Officer" className="bg-slate-700 border-slate-600 text-white" data-testid="rename-label-input" />
                </div>
                <Button onClick={runRename} disabled={renaming}
                  className="bg-red-700 hover:bg-red-600 text-white" data-testid="rename-run-btn">
                  {renaming ? <Loader2 className="w-4 h-4 animate-spin" /> : "Rename"}
                </Button>
                <p className="text-slate-500 text-xs sm:col-span-4">
                  Updates every list below plus member profiles, hub logins, permission roles, email groups, and events in one shot.
                </p>
              </CardContent>
            </Card>

            {Object.keys(LIST_META).map((name) => (
              <Card key={name} className="bg-slate-800/50 border-slate-700" data-testid={`titlelist-${name}`}>
                <CardContent className="p-4">
                  <p className="text-white font-semibold">{LIST_META[name][0]}</p>
                  <p className="text-slate-500 text-xs mb-2">{LIST_META[name][1]}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(lists[name] || []).map((t) => (
                      <span key={t}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-900/60 border border-slate-600 text-slate-200">
                        {t}
                        <button onClick={() => removeFrom(name, t)} className="text-slate-500 hover:text-red-400"
                          data-testid={`remove-${name}-${t}`} title="Remove from this list">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    <span className="inline-flex items-center gap-1">
                      <Input value={addInputs[name] || ""}
                        onChange={(e) => setAddInputs((a) => ({ ...a, [name]: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && addTo(name)}
                        placeholder="Add…"
                        className="bg-slate-700 border-slate-600 text-white h-7 w-24 text-xs"
                        data-testid={`add-input-${name}`} />
                      <button onClick={() => addTo(name)} className="text-amber-500 hover:text-amber-400"
                        data-testid={`add-btn-${name}`}>
                        <Plus className="w-4 h-4" />
                      </button>
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Layers className="w-4 h-4 text-amber-500" /> Title ↔ Chapter Assignment
                </CardTitle>
                <p className="text-slate-500 text-xs">Pick which chapters hold each title. No chapters selected = available in ALL chapters. Applies to member Title dropdowns and the Permissions page tabs. Hit "Save Lists" to apply.</p>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="title-chapter-assignment">
                {(lists.all_titles || []).map((t) => {
                  const sel = chapterMap[t] || [];
                  return (
                    <div key={t} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-slate-900/40 border border-slate-700" data-testid={`assign-row-${t}`}>
                      <div className="min-w-0">
                        <span className="text-white text-sm font-semibold">{t}</span>
                        {labels[t] && labels[t] !== t && <span className="text-slate-500 text-xs ml-1.5 hidden sm:inline">{labels[t]}</span>}
                      </div>
                      <div className="flex flex-wrap gap-1 justify-end items-center">
                        <span className={`text-[10px] mr-1 ${sel.length ? "text-slate-600" : "text-emerald-500/80 font-semibold"}`}>
                          {sel.length ? "" : "All chapters"}
                        </span>
                        {activeChapters.map((c) => (
                          <button key={c} type="button" onClick={() => toggleMapChapter(t, c)}
                            className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors ${
                              sel.includes(c)
                                ? "bg-amber-600 border-amber-500 text-white"
                                : "bg-slate-800 border-slate-700 text-slate-500 hover:text-white"
                            }`}
                            data-testid={`assign-${t}-${c}`}>{c}</button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-red-900/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Trash2 className="w-4 h-4 text-red-500" /> Title Cleanup
                </CardTitle>
                <p className="text-slate-500 text-xs">Delete old titles for good — removed from every list, label, chapter assignment, and permission row. Titles still held by a member or hub login are protected: reassign them (or use Rename) first.</p>
              </CardHeader>
              <CardContent className="space-y-1.5" data-testid="title-cleanup-list">
                {allKnown.map((t) => {
                  const u = usage[t] || {};
                  const inUse = (u.members || 0) + (u.users || 0) > 0;
                  return (
                    <div key={t} className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg bg-slate-900/40 border border-slate-700" data-testid={`cleanup-row-${t}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-white text-sm font-semibold truncate">{t}</span>
                        {labels[t] && labels[t] !== t && <span className="text-slate-500 text-xs hidden sm:inline truncate">{labels[t]}</span>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`flex items-center gap-1 text-xs ${inUse ? "text-amber-500" : "text-slate-600"}`} data-testid={`cleanup-usage-${t}`}>
                          <Users className="w-3 h-3" /> {u.members || 0}{u.users ? ` · ${u.users} login${u.users > 1 ? "s" : ""}` : ""}
                        </span>
                        <button onClick={() => deleteTitle(t)} disabled={inUse || deleting === t}
                          className={inUse ? "text-slate-700 cursor-not-allowed" : "text-red-500 hover:text-red-400"}
                          title={inUse ? "Still in use — reassign or rename first" : "Delete everywhere"}
                          data-testid={`cleanup-delete-${t}`}>
                          {deleting === t ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <p className="text-slate-500 text-xs">Changes take effect after "Save Lists". Removing a title from a list only changes access — it does not touch member records (use Rename for that).</p>
          </>
        )}
      </div>
    </div>
  );
}
